<template>
  <div class="timetable" :class="type">
    <div class="controls sticky">
      <button class="uk-button uk-button-primary icon-button control-left" @click="slide('prev')"><div class="icon gg-chevron-left" /></button>
      <multiselect v-model="filter" class="date-select" :options="days" :custom-label="selectLabel" label="date" track-by="id" placeholder="" :show-labels="false" :allow-empty="false" :preselect-first="true" @select="slideTo" />
      <button class="uk-button uk-button-primary icon-button control-right" @click="slide('next')"><div class="icon gg-chevron-right" /></button>
    </div>

    <hooper ref="labels" class="carousel sticky" :settings="hooperSettings">
      <hooper-slide v-for="day in days" :key="day.index" :index="day.index">
        <div class="label">
          <div class="day">{{ day.date.format('dddd') }}</div>
          <div class="date">{{ day.date.format('DD/MM') }}</div>
          <div v-if="type === 'edit'" class="tools">
            <div class="button" data-cy="day" @click="toggleDay(day.date)">All Day</div>
          </div>
        </div>
      </hooper-slide>
    </hooper>

    <hooper ref="bits" class="carousel" :settings="hooperSettings">
      <hooper-slide v-for="day in days" :key="day.index" :index="day.index">
        <div
          v-for="bit in $helpers.timeBits"
          :key="bit.index"
          class="bit"
          :class="bitStatus(day, bit).class"
          :uk-tooltip="`title: ${day.date.format('DD/MM/YYYY')}<br>${bit.time.long}<br>${bitStatus(day, bit).tooltip}; delay: 1000`"
          :data-cy="`bit-${day.date.format('DDMM')}`"
          @click="selectBit(day.date, bit.id)"
        >
          {{ bit.time.short }}
        </div>
      </hooper-slide>
    </hooper>
  </div>
</template>

<script>
export default {
  name: 'ComponentTimetable',

  props: {
    data: {
      type: Object,
      required: true
    },
    type: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      availability: {},
      days: [],
      filter: null,
      hooperSettings: {
        itemsToShow: 3,
        itemsToSlide: 3,
        centerMode: true,
        trimWhiteSpace: true,
        mouseDrag: false,
        touchDrag: false,
        wheelControl: false,
        breakpoints: {
          360: {
            itemsToShow: 4,
            itemsToSlide: 4,
            centerMode: false
          },
          440: {
            itemsToShow: 5,
            itemsToSlide: 5,
            centerMode: false
          },
          520: {
            itemsToShow: 6,
            itemsToSlide: 6,
            centerMode: false
          },
          600: {
            itemsToShow: 7,
            itemsToSlide: 7
          }
        }
      }
    }
  },

  computed: {
    // Formatted Availability
    formattedAvailability() {
      // Remove empty days
      const availability = { ...this.availability }
      Object.keys(availability).forEach((key) => (Object.entries(availability[key]).length === 0 && availability[key].constructor === Object) && delete availability[key])
      return availability
    }
  },

  created() {
    const range = this.$moment.rangeFromInterval('day', 27, this.$moment())
    const days = Array.from(range.by('day'))
    const emptyAvailability = {}

    days.forEach((v, i) => {
      this.days.push({
        id: i,
        date: v
      })

      emptyAvailability[v.format('YYMMDD')] = {}
    })

    const t = { ...emptyAvailability, ...this.data }
    this.$set(this, 'availability', t)
  },

  mounted() {
    this.$refs.labels.updateWidth()
  },

  methods: {
    checkBit(day, bit) {
      const date = day.format('YYMMDD')
      const current = {}
      const time = this.$moment()
      current.bit = time.hour() * 2 + 1
      current.date = time.format('YYMMDD')

      if (time.minute() >= 30) {
        current.bit += 1
      }

      const future = bit > current.bit || date !== current.date
      const past = bit <= current.bit && date === current.date

      return { future, past }
    },

    bitStatus(day, bit) {
      const availability = this.availability[day.date.format('YYMMDD')]
      const status = {
        class: {
          appointment: false,
          available: false,
          past: false
        },
        tooltip: 'Not Available'
      }

      const check = {
        exists: availability.hasOwnProperty(bit.id),
        state: availability[bit.id] || 0
      }

      // "Appointment" Class
      if (check.exists && check.state === 2) {
        status.class.appointment = true
        status.tooltip = 'Appointment'
      }

      // "Available" Class
      if (check.exists && check.state === 1) {
        status.class.available = true
        status.tooltip = 'Available'
      }

      // "Past" Class
      if (this.type === 'edit' && this.checkBit(day.date, bit.id).past) {
        status.class.past = true
      }

      return status
    },

    selectLabel(day) {
      return day.date.format('DD/MM')
    },

    slide(direction) {
      if (direction === 'prev') {
        this.$refs.labels.slidePrev()
        this.$refs.bits.slidePrev()
      }

      if (direction === 'next') {
        this.$refs.labels.slideNext()
        this.$refs.bits.slideNext()
      }
    },

    slideTo(selected) {
      const index = selected.id < 2 ? 3 : selected.id

      this.$refs.labels.slideTo(index)
      this.$refs.bits.slideTo(index)
    },

    selectBit(day, bit) {
      if (this.type === 'edit' && this.checkBit(day, bit).future) {
        const date = day.format('YYMMDD')
        const status = this.availability[date][bit]

        if (!status) {
          this.$set(this.availability[date], bit, 1)
        }

        if (status === 1) {
          this.$delete(this.availability[date], bit)
        }
      }
    },

    toggleDay(day) {
      const date = day.format('YYMMDD')

      for (let bit = 1; bit <= 48; bit += 1) {
        const status = this.availability[date][bit] || 0

        if (this.type === 'edit' && this.checkBit(day, bit).future) {
          if (status === 0) {
            this.$set(this.availability[date], bit, 1)
          }

          if (status === 1) {
            this.$set(this.availability[date], bit, 0)
          }
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.timetable {
  width: 100%;
  max-width: 600px;
  text-align: center;

  .controls {
    display: flex;
    align-items: center;
    justify-content: space-around;

    .date-select {
      max-width: 100px;
    }
  }

  .controls.sticky,
  .carousel.sticky {
    position: sticky;
    top: -30px;
  }

  .controls.sticky {
    z-index: 6;
    padding: 10px;
    background-color: $colour-light;
  }

  .carousel.sticky {
    z-index: 5;
    top: 30px;
  }

  .carousel {
    height: auto;
    outline: none;

    .label {
      padding: 4px 8px;
      border: 1px solid #fff;
      background-color: $colour-primary;
      color: #fff;

      .day {
        font-size: .8em;
        font-weight: 500;
      }

      .date {
        font-size: 1.1em;
      }

      .tools {
        .button {
          display: inline-block;
          padding: 0 4px;
          transition: background-color 200ms ease;
          background-color: $colour-light;
          color: $colour-primary;
          font-size: .75em;
          text-transform: uppercase;
          cursor: pointer;
          user-select: none;

          &:hover {
            background-color: $colour-grey;
          }
        }
      }
    }

    .bit {
      padding: 6px;
      transition: background-color 100ms ease;
      border: 1px solid $colour-light;
      background-color: $colour-grey;
      cursor: help;
      user-select: none;

      &:hover {
        background-color: darken($colour-grey, 5%);
      }

      &.available {
        background-color: $colour-primary;
        color: $colour-light;

        &:hover {
          background-color: darken($colour-primary, 10%);
        }
      }

      &.appointment {
        background-color: $colour-success;
        color: $colour-light;

        &:hover {
          background-color: darken($colour-success, 10%);
        }
      }

      &.past {
        opacity: .5;
        cursor: not-allowed;
      }
    }
  }

  &.edit {
    .bit {
      cursor: pointer;
    }
  }
}
</style>

<style lang="scss">
.date-select {
  .multiselect__option--selected.multiselect__option--highlight {
    background-color: $colour-primary;
  }
}
</style>
